import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { getStorage } from '@src/utils/enhancedStorage'

import { FEATURES, MODULES } from '../constants/template-info'
import QuinceApiError from '../domains/quince-api-error'
import wishListStateManager from '../redux/globalStateManager/WishlistStateManager'
import AuthUtil from '../utils/authUtil'
import { storefrontSandProductServiceHttpClient, storefrontSandProductServiceAuthHttpClient } from '../utils/HttpClient'

import { ACTION, SharedWishlistRes, WishListItem } from './Wishlist.types'

// @i18n-ignore
export const WISHLIST_LOCAL_STORAGE_KEY = 'wishlist'

class WishlistService {
  /**
   * A method that accepts a product and color and returns true if this product color combination is present in
   * local storage wishlist.
   */
  isItemInWishlist(spid: string, color: string): boolean {
    const wishlistItemToBeSearched = new WishListItem({
      product_id: spid,
      color: color,
    })
    return this.fetchUserWishListFromLocalStorage().some(wli => WishListItem.factEquals(wli, wishlistItemToBeSearched))
  }

  /**
   * On Login, combining wishlist from local storage and backend.
   */
  async updateWishListOnLoggedIn(): Promise<WishListItem[]> {
    const wishListItems = await this.fetchWishlistFromBackend(AuthUtil.getUser().email || '')
    const combinedWishListItems = getCombinedWishlist(wishListItems, this.fetchUserWishListFromLocalStorage())
    wishListStateManager.setWishlistTotalItemsCount(combinedWishListItems.length)
    // In case combined list is same as backend list, then skipping backend update.
    await this.updateWishListItems(combinedWishListItems, combinedWishListItems.length == wishListItems.length)
    return combinedWishListItems
  }

  /**
   * On Already logged in, return wishlist from backend.
   */
  async wishListAlreadyLoggedIn(email: string): Promise<WishListItem[]> {
    const wishListItems = await this.fetchWishlistFromBackend(email)
    await this.updateWishListItems(wishListItems, true)
    return wishListItems
  }

  getWishlistAfterRemovingStaleItems(wishListItems: WishListItem[] = []) {
    return wishListItems.filter((wishListItem: WishListItem) => {
      //TODO: update when website light product is fully migrated to v2
      const availableColors = Object.values(wishListItem.website_light_product?.productColors)
      if (availableColors) {
        const foundColor = availableColors.find(color => color.color === wishListItem.color)
        return !!foundColor
      }
      return false
    })
  }

  getWishlistTotalItemsCount(): number {
    return this.fetchUserWishListFromLocalStorage().length
  }

  /**
   * This returns wishlist from Local Storage.
   */
  fetchUserWishListFromLocalStorage(): WishListItem[] {
    const wishListItemsString = getStorage().getItem<string>(WISHLIST_LOCAL_STORAGE_KEY, { isGlobal: false })
    let wishListItems = []
    try {
      if (wishListItemsString) {
        wishListItems = JSON.parse(wishListItemsString)
      }
      return wishListItems.map((wli: WishListItem) => {
        return new WishListItem({ product_id: wli.product_id, color: wli.color })
      })
    } catch (e) {
      // In case error in parsing of local storage key, then setting it empty.
      wishListItems = []
      this.updateWishListItems([], true).catch((e: unknown) => {
        console.error(e)
      })
    }
    return []
  }

  async addItemToWishlist(spid: string, color: string) {
    const isPresentInWishList = this.isItemInWishlist(spid, color)
    if (isPresentInWishList) {
      // If already present in wishlist, then ignoring
      return
    }
    const wishListItems = this.fetchUserWishListFromLocalStorage()
    wishListItems.push(
      new WishListItem({
        product_id: spid,
        color: color,
      }),
    )
    this.addRemoveUserWishlistInLocalStorage(wishListItems)
    await this.addRemoveUserWishlistInBackend(
      {
        spid,
        color: color,
      },
      ACTION.ADD,
    )
  }

  async removeFromWishlist(spid: string, color: string) {
    const isPresentInWishList = this.isItemInWishlist(spid, color)
    if (!isPresentInWishList) {
      // If item is not present we dont need to remove it.
      return
    }
    const toRemoveWishListItem = new WishListItem({
      product_id: spid,
      color: color,
    })
    const updatedWishListItems = this.fetchUserWishListFromLocalStorage().filter(
      wli => !WishListItem.factEquals(wli, toRemoveWishListItem),
    )
    this.addRemoveUserWishlistInLocalStorage(updatedWishListItems)
    await this.addRemoveUserWishlistInBackend(
      {
        spid,
        color: color,
      },
      ACTION.REMOVE,
    )
  }

  private async updateWishListItems(updatedWishListItems: WishListItem[], skipBackendUpdate = false) {
    const sanitizedWishList = updatedWishListItems.map(item => new WishListItem(item))
    updateWishListInLocalStorage(sanitizedWishList)

    const user = AuthUtil.getUser()
    if (AuthUtil.isLoggedIn() && user.email && !skipBackendUpdate) {
      await this.updateUserWishlistInBackend(sanitizedWishList, user.email)
    }
  }

  private updateUserWishlistInBackend = async (updatedWishListItems: WishListItem[], email: string) => {
    try {
      const productIdColors = updatedWishListItems.map(wli => `${wli.product_id}_${wli.color}`).join(',')
      await storefrontSandProductServiceAuthHttpClient.post(`/bulk-update-user-wishlist`, null, {
        params: {
          productIdColors: productIdColors,
        },
      })
    } catch (err) {
      const quinceApiError = new QuinceApiError({
        apiName: 'UPDATE_USER_WISHLIST',
        // @i18n-ignore
        message: 'FAILED TO UPDATE WISHLIST',
        error: err as Error,
        data: {
          email,
        },
      })
      captureException(quinceApiError, {
        module: MODULES.WISHLIST,
        feature: FEATURES.UPDATE_WISHLIST,
      })
    }
  }

  private addRemoveUserWishlistInLocalStorage = (updatedWishListItems: WishListItem[]) => {
    const sanitizedWishList = updatedWishListItems.map(item => new WishListItem(item))
    updateWishListInLocalStorage(sanitizedWishList)
  }

  private addRemoveUserWishlistInBackend = async (wishListItem: any, action: string) => {
    const user = AuthUtil.getUser()
    if (!(AuthUtil.isLoggedIn() && user.email)) {
      return
    }
    try {
      await storefrontSandProductServiceAuthHttpClient.post(`/update-user-wishlist-v3`, null, {
        params: {
          ...wishListItem,
          action,
        },
      })
    } catch (err) {
      const quinceApiError = new QuinceApiError({
        apiName: 'TOGGLE_USER_WISHLIST',
        // @i18n-ignore
        message: 'FAILED TO TOGGLE WISHLIST',
        error: err as Error,
        data: {
          email: user.email,
        },
      })
      captureException(quinceApiError, {
        module: MODULES.WISHLIST,
        feature: FEATURES.TOGGLE_WISHLIST,
      })
    }
  }

  /**
   * This method fetches wishlist item for a logged in user from redis cache.
   * @private
   */
  private fetchWishlistFromBackend = async (email: string): Promise<WishListItem[]> => {
    try {
      const response = await storefrontSandProductServiceAuthHttpClient.get<{
        total: number
        data: WishListItem[]
      }>('/get-active-wishlist-products')
      const wishlistItems = response.data.data
      return wishlistItems
    } catch (err) {
      const quinceApiError = new QuinceApiError({
        apiName: 'GET_ACTIVE_WISHLIST_PRODUCTS',
        // @i18n-ignore
        message: 'FAILED TO GET USER WISHLIST',
        error: err as Error,
        data: {
          email,
        },
      })
      captureException(quinceApiError, {
        module: MODULES.WISHLIST,
        feature: FEATURES.FETCH_WISHLIST,
      })

      return []
    }
  }

  getWishlistToken = async () => {
    try {
      const { data } = await storefrontSandProductServiceAuthHttpClient.get('/get-wishlist-token')
      return data
    } catch (error) {
      console.error('Error While Getting Wishlist Token ', error)
      return {
        wishlist_token: '',
      }
    }
  }

  async getSharedWishlist(id: string): Promise<SharedWishlistRes> {
    try {
      const { data } = await storefrontSandProductServiceHttpClient.get('/get-shared-wishlist?wishlist_token=' + id)
      return data
    } catch (error) {
      console.error('error while getting the shared wishlist', error)
      return {}
    }
  }
}

const updateWishListInLocalStorage = (wishListItems: WishListItem[]) => {
  getStorage().setItem(WISHLIST_LOCAL_STORAGE_KEY, JSON.stringify(wishListItems), { isGlobal: false })
}

/**
 * This method combines wishlist items from currentWishlistInBrowserStorage and wishlistFromApi and returns
 * combined wishlistItems.
 */
const getCombinedWishlist = (
  wishlistFromApi: WishListItem[],
  currentWishlistInBrowserStorage: WishListItem[],
): WishListItem[] => {
  const wishListIdSet = new Set<string>()
  const combinedList: WishListItem[] = []
  ;[...currentWishlistInBrowserStorage, ...wishlistFromApi].forEach(wli => {
    const id = WishListItem.getWishListId(wli)
    if (!wishListIdSet.has(id)) {
      wishListIdSet.add(id)
      combinedList.push(wli)
    }
  })
  return combinedList
}

const wishlistService = new WishlistService()

export default wishlistService
